import { supabase } from "@supabaseClient/supabaseClient";
import { CustomersClient } from "@Clients/CustomersClient";

export interface IOrder {
  id: string;
  order_number: string;
  customer_id?: string | null;
  customer_name: string;
  customer_email?: string;
  status: "open" | "processing" | "completed" | "cancelled";
  notes?: string;
  created_by: string;
  organization_id: string;
  backorder_status: "none" | "partial" | "full" | "fulfilled";
  backorder_created_at?: string;
  backorder_fulfilled_at?: string;
  status_changed_at?: string;
  status_changed_by?: string;
  created_at: string;
  updated_at: string;
  po_number?: string;
  due_date?: string;
  customer_reference?: string;
  metadata?: Record<string, string | number | boolean | null | undefined>;
}

export interface IOrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity_requested: number;
  quantity_fulfilled: number;
  backorder_quantity: number;
  fulfillment_priority: number;
  status: "pending" | "partially_fulfilled" | "fulfilled" | "cancelled";
  notes?: string;
  created_at: string;
  updated_at: string;
  organization_id: string;
}

export interface IOrderWithItems extends IOrder {
  order_items: IOrderItem[];
}

export interface IOrderWithCustomer extends IOrder {
  customer?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
}

export interface IOrderWithItemsAndCustomer extends IOrderWithItems {
  customer?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
  } | null;
}

export interface IBackorderQueue {
  id: string;
  order_id: string;
  order_item_id: string;
  product_id: string;
  quantity_needed: number;
  priority: number;
  status: "pending" | "in_progress" | "fulfilled" | "cancelled";
  organization_id: string;
  created_at: string;
  updated_at: string;
}

export class OrdersClient {
  static async getOrdersByOrganizationId(
    organization_id: string,
    includeCustomer: boolean = false
  ): Promise<IOrderWithItems[] | IOrderWithItemsAndCustomer[]> {
    console.log("Fetching orders from Supabase for org:", organization_id);

    const selectQuery = includeCustomer
      ? `*, order_items (*), customer:customers!customer_id (id, name, email, phone)`
      : `*, order_items (*)`;

    const { data, error } = await supabase
      .from("orders")
      .select(selectQuery)
      .eq("organization_id", organization_id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Supabase error:", error);
      throw error;
    }
    console.log("Supabase response:", { data, error });
    return (data || []) as unknown as
      | IOrderWithItems[]
      | IOrderWithItemsAndCustomer[];
  }

  static async getOrderById(
    order_id: string,
    includeCustomer: boolean = false
  ): Promise<IOrderWithItems | IOrderWithItemsAndCustomer> {
    const selectQuery = includeCustomer
      ? `*, order_items (*), customer:customers!customer_id (id, name, email, phone)`
      : `*, order_items (*)`;

    const { data, error } = await supabase
      .from("orders")
      .select(selectQuery)
      .eq("id", order_id)
      .single();

    if (error) {
      throw error;
    }
    return data as unknown as
      | IOrderWithItems
      | IOrderWithItemsAndCustomer;
  }

  /**
   * Create a new order
   * If customer_id is not provided but customer_name/email are, will find or create customer
   */
  static async createOrder(
    order: Omit<IOrder, "id" | "order_number" | "created_at" | "updated_at"> & {
      order_number?: string;
    }
  ): Promise<IOrder> {
    // Generate order number if one was not provided
    const orderNumber =
      order.order_number?.trim() || (await this.generateOrderNumber());

    let customerId = order.customer_id;

    // If no customer_id but we have customer info, find or create customer
    // (for backward compatibility with legacy orders)
    if (!customerId && (order.customer_name || order.customer_email)) {
      try {
        const customer = await CustomersClient.findOrCreateCustomer({
          organization_id: order.organization_id,
          name: order.customer_name || "",
          email: order.customer_email || null,
          phone: null,
        });
        customerId = customer.id;
      } catch (error) {
        console.warn(
          "Failed to find/create customer, proceeding without customer_id:",
          error
        );
        // Continue without customer_id - database trigger won't populate fields
      }
    }

    // Database trigger will automatically populate customer_name and customer_email
    // from customers table when customer_id is provided
    type OrderInsertData = Omit<
      IOrder,
      "id" | "order_number" | "created_at" | "updated_at"
    > & {
      order_number: string;
      customer_id: string | null;
      customer_name?: string;
      customer_email?: string | null;
    };

    const orderData: OrderInsertData = {
      ...order,
      order_number: orderNumber,
      customer_id: customerId || null,
    };

    // Only include customer_name/customer_email if customer_id is not provided (legacy support)
    if (!customerId) {
      orderData.customer_name = order.customer_name || "";
      orderData.customer_email = order.customer_email;
    }
    // If customer_id is provided, don't send customer_name/customer_email - trigger will populate them

    const { data, error } = await supabase
      .from("orders")
      .insert([orderData])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  }

  static async updateOrder(
    order: Omit<IOrder, "created_at" | "updated_at">
  ): Promise<IOrder> {
    // Database trigger will automatically populate customer_name and customer_email
    // from customers table when customer_id is provided
    type OrderUpdateData = Omit<
      IOrder,
      "created_at" | "updated_at" | "customer_name" | "customer_email"
    > & {
      customer_name?: string;
      customer_email?: string | null;
    };

    const orderData: OrderUpdateData = {
      ...order,
      customer_name: undefined,
      customer_email: undefined,
    };

    // Only include customer_name/customer_email if customer_id is not provided (legacy support)
    if (!order.customer_id) {
      orderData.customer_name = order.customer_name || "";
      orderData.customer_email = order.customer_email;
    }
    // If customer_id is provided, don't send customer_name/customer_email - trigger will populate them

    const { data, error } = await supabase
      .from("orders")
      .update(orderData)
      .eq("id", order.id)
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  }

  static async deleteOrder(id: string): Promise<void> {
    const { error } = await supabase.from("orders").delete().eq("id", id);
    if (error) {
      throw error;
    }
  }

  static async createOrderItem(
    orderItem: Omit<IOrderItem, "id" | "created_at" | "updated_at">
  ): Promise<IOrderItem> {
    const { data, error } = await supabase
      .from("order_items")
      .insert([orderItem])
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  }

  static async updateOrderItem(
    orderItem: Omit<IOrderItem, "created_at" | "updated_at">
  ): Promise<IOrderItem> {
    const { data, error } = await supabase
      .from("order_items")
      .update(orderItem)
      .eq("id", orderItem.id)
      .select();

    if (error) {
      throw error;
    }
    return data[0];
  }

  static async deleteOrderItem(id: string): Promise<void> {
    const { error } = await supabase.from("order_items").delete().eq("id", id);
    if (error) {
      throw error;
    }
  }

  private static async generateOrderNumber(): Promise<string> {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");

    // Get the next sequence number for this month
    const { data: lastOrder, error } = await supabase
      .from("orders")
      .select("order_number")
      .like("order_number", `${year}-${month}-%`)
      .order("order_number", { ascending: false })
      .limit(1);

    if (error) {
      throw error;
    }

    let nextSequence = 1;
    if (lastOrder && lastOrder.length > 0) {
      const lastNumber = lastOrder[0].order_number;
      const lastSequence = parseInt(lastNumber.split("-")[2]);
      nextSequence = lastSequence + 1;
    }

    return `${year}-${month}-${nextSequence.toString().padStart(3, "0")}`;
  }

  // Update order status
  async updateOrderStatus(
    orderId: string,
    status: "open" | "processing" | "completed" | "cancelled",
    userId: string,
    organizationId: string
  ): Promise<IOrder> {
    const { data, error } = await supabase
      .from("orders")
      .update({
        status,
        status_changed_at: new Date().toISOString(),
        status_changed_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", orderId)
      .eq("organization_id", organizationId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update order status: ${error.message}`);
    }

    return data;
  }

  // Get orders by status
  async getOrdersByStatus(
    status: "open" | "processing" | "completed" | "cancelled",
    organizationId: string
  ): Promise<IOrder[]> {
    const { data, error } = await supabase
      .from("orders")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("status", status)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch orders by status: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Link an order to a customer
   * Useful for linking existing orders to customers
   */
  static async linkOrderToCustomer(
    orderId: string,
    customerId: string
  ): Promise<IOrder> {
    const { data, error } = await supabase
      .from("orders")
      .update({ customer_id: customerId })
      .eq("id", orderId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to link order to customer: ${error.message}`);
    }

    return data;
  }

  /**
   * Find or create customer and link to order
   * Useful when updating an existing order with customer info
   */
  static async linkOrderToCustomerByInfo(
    orderId: string,
    customerInfo: {
      organization_id: string;
      name: string;
      email?: string | null;
      phone?: string | null;
    }
  ): Promise<{
    order: IOrder;
    customer: import("@Clients/CustomersClient").ICustomer;
  }> {
    // Find or create customer
    const customer = await CustomersClient.findOrCreateCustomer(customerInfo);

    // Link order to customer
    const order = await this.linkOrderToCustomer(orderId, customer.id);

    return { order, customer };
  }
}
